import { useState, type ReactNode } from 'react';
import { Alert, Badge, Button, Container, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { fetchSecretRequestValue } from '@/apps/apis/secrets';
import { useSecretsAccess } from '@/apps/apis/secrets/hooks';
import { SECRET_KEY_PATTERN, SECRET_VALUE_MAX_BYTES } from '@/apps/apis/secrets/types';
import type { SecretRequestItem, SecretsMe } from '@/apps/apis/secrets/types';
import { ROUTES } from '@/routes/routes';

export const asArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);

export const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

export const errorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export const readableError = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof message === 'string' && message) {
    return message;
  }
  if (Array.isArray(message) && message.length > 0) {
    return message.join(', ');
  }
  return error instanceof Error && error.message ? error.message : fallback;
};

/** Client-side mirror of the server rules; the server stays authoritative. */
export const fieldProblem = (key: string, value?: string): string | null => {
  if (!SECRET_KEY_PATTERN.test(key)) {
    return `${key || '(empty key)'}: keys may use letters, digits and _, not starting with a digit.`;
  }
  if (value !== undefined && new TextEncoder().encode(value).length > SECRET_VALUE_MAX_BYTES) {
    return `${key}: value exceeds ${SECRET_VALUE_MAX_BYTES} bytes.`;
  }
  return null;
};

const normalizeEmail = (value?: string | null): string => value?.trim().toLowerCase() ?? '';

export const sameEmail = (left?: string | null, right?: string | null): boolean =>
  normalizeEmail(left) !== '' && normalizeEmail(left) === normalizeEmail(right);

/** Renders children only once /secrets/me answers; any failure means the API is absent or refuses this user. */
export const SecretsGate = ({ children }: { children: (me: SecretsMe) => ReactNode }) => {
  const access = useSecretsAccess();
  if (access.status === 'loading') {
    return (
      <Container fluid className="py-4">
        <Spinner animation="border" size="sm" />
      </Container>
    );
  }
  if (access.status === 'unavailable') {
    return (
      <Container fluid className="py-4">
        <Alert variant="secondary">Secrets are not available for your account in this environment.</Alert>
      </Container>
    );
  }
  return <>{children(access.me)}</>;
};

const STATUS_VARIANT: Record<string, string> = {
  PENDING: 'warning',
  APPROVED: 'info',
  APPLIED: 'success',
  REJECTED: 'secondary',
  CONFLICT: 'danger',
};

interface SecretRequestsTableProps {
  items: SecretRequestItem[];
  me: SecretsMe;
  showService?: boolean;
  renderActions?: ((item: SecretRequestItem) => ReactNode) | undefined;
}

/** Request/history rows. Viewed values live in this component's state only and vanish with it. */
export const SecretRequestsTable = ({ items, me, showService, renderActions }: SecretRequestsTableProps) => {
  const [values, setValues] = useState<Record<string, string | null>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const viewValue = async (requestId: string) => {
    setLoadingId(requestId);
    setError(null);
    try {
      const result = await fetchSecretRequestValue(requestId);
      setValues((previous) => ({ ...previous, [requestId]: result?.value ?? null }));
    } catch (caught) {
      setError(readableError(caught, 'Unable to load the requested value.'));
    } finally {
      setLoadingId(null);
    }
  };

  if (items.length === 0) {
    return <p className="text-muted mb-0">No requests.</p>;
  }

  return (
    <>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      <Table size="sm" responsive hover className="align-middle mb-0">
        <thead>
          <tr>
            <th>Created</th>
            {showService ? <th>Service</th> : null}
            <th>Op</th>
            <th>Key</th>
            <th>Value</th>
            <th>Status</th>
            <th>Source</th>
            <th>Requester</th>
            <th>Reason</th>
            <th>Decided</th>
            {renderActions ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const requestId = item?.requestId ?? '';
            const canView = item?.op !== 'DELETE' && (me.canManage || sameEmail(item?.requesterEmail, me.email));
            const viewed = requestId in values;
            return (
              <tr key={requestId}>
                <td className="text-nowrap">{formatDate(item?.createdAt)}</td>
                {showService ? (
                  <td>{item?.path ? <Link to={ROUTES.secrets.detail(item.path)}>{item.path}</Link> : '—'}</td>
                ) : null}
                <td>{item?.op ?? '—'}</td>
                <td>
                  <code>{item?.key ?? '—'}</code>
                </td>
                <td>
                  {viewed ? (
                    <code className="text-break">{values[requestId] ?? '(restricted)'}</code>
                  ) : canView ? (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={loadingId === requestId}
                      onClick={() => void viewValue(requestId)}
                    >
                      View value
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <Badge bg={STATUS_VARIANT[item?.status ?? ''] ?? 'secondary'}>{item?.status ?? '—'}</Badge>
                </td>
                <td>{item?.source ?? '—'}</td>
                <td>{item?.requesterEmail ?? '—'}</td>
                <td className="text-break">{item?.reason ?? '—'}</td>
                <td className="text-nowrap">
                  {item?.decidedBy ? `${item.decidedBy} · ${formatDate(item?.decidedAt)}` : '—'}
                </td>
                {renderActions ? <td className="text-nowrap">{renderActions(item)}</td> : null}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};
